import { createContext, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTokenData } from "@/lib/tokenApi";
import { formatCompactUsd } from "@/lib/format";
import { UNLOCK_MARKET_CAP, TOKEN_REFRESH_MS } from "@/config/kingbullish";
import { TOKEN_STATUS } from "@/config/tokenData";

export const UNLOCK_LABEL = formatCompactUsd(UNLOCK_MARKET_CAP);
export const lockedMessage = (kind = "product") =>
  `This ${kind} unlocks when KINGBULLISH reaches ${UNLOCK_LABEL} market cap.`;

const TokenDataContext = createContext(null);

export const TokenDataProvider = ({ children }) => {
  const query = useQuery({
    queryKey: ["token"],
    queryFn: getTokenData,
    refetchInterval: TOKEN_REFRESH_MS,
    retry: 2,
  });
  const [simulatedMarketCap, setSimulatedMarketCap] = useState(null);

  const liveMarketCap = query.data?.currentMarketCap ?? null;
  // ONE source of truth: token bar, roadmap, ticker, ecosystem & portfolios all read this.
  const marketCap = simulatedMarketCap ?? liveMarketCap;
  // ONE unlock calculation for the whole app.
  const isUnlocked = marketCap !== null && marketCap >= UNLOCK_MARKET_CAP;
  const isPreLaunch = simulatedMarketCap === null && query.data?.status === TOKEN_STATUS.LAUNCH_PENDING;

  const value = useMemo(
    () => ({
      token: query.data ?? null,
      isLoading: query.isLoading,
      isError: query.isError,
      marketCap,
      liveMarketCap,
      isUnlocked,
      isPreLaunch,
      unlockMarketCap: UNLOCK_MARKET_CAP,
      // progress = currentMarketCap / milestone — drives every roadmap bar and the $10M unlock
      progressTo: (cap) => (marketCap === null ? 0 : Math.min(marketCap / cap, 1)),
      progress: marketCap === null ? 0 : Math.min(marketCap / UNLOCK_MARKET_CAP, 1),
      isSimulated: simulatedMarketCap !== null,
      simulatedMarketCap,
      setSimulatedMarketCap,
    }),
    [query.data, query.isLoading, query.isError, marketCap, liveMarketCap, isUnlocked, isPreLaunch, simulatedMarketCap],
  );

  return <TokenDataContext.Provider value={value}>{children}</TokenDataContext.Provider>;
};

export const useTokenData = () => useContext(TokenDataContext);

export const useUnlock = () => {
  const { isUnlocked, isPreLaunch, marketCap, unlockMarketCap, progress, progressTo } = useTokenData();
  return { isUnlocked, isPreLaunch, marketCap, unlockMarketCap, progress, progressTo, unlockLabel: UNLOCK_LABEL };
};
